import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Users } from "lucide-react";
import { STAGES } from "../data/mockData";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";

export default function Learn() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { enrollments } = useApp();

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/content/courses");
        if (!ignore) setCourses(data || []);
      } catch {}
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  const progressFor = (courseId) => {
    const e = enrollments.find((x) => x.course_id === courseId);
    return e ? e.progress : 0;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="mb-10 rise">
        <div className="eyebrow mb-2">The Learning Circle</div>
        <h1 className="font-serif text-4xl sm:text-5xl" style={{ color: "var(--burgundy)" }}>Walk the eight stages.</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--text-muted)" }}>
          From ENTER to RETURN — an interior curriculum for contemplative practice. Move at your own pace. The path is not a ladder; it is a spiral.
        </p>
      </div>

      <div className="rounded-3xl p-6 sm:p-10 border mb-14 relative overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <div className="grain absolute inset-0" />
        <div className="relative overflow-x-auto -mx-2 px-2">
          <div className="min-w-[860px] grid grid-cols-8 gap-3 items-start">
            {STAGES.map((s, i) => (
              <div key={s.step} className="text-center" data-testid={`learn-stage-${s.name.toLowerCase()}`}>
                <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center font-mono text-sm" style={{ background: "var(--bg-cream)", color: "var(--burgundy)", border: `1.5px solid var(--gold-soft)` }}>
                  {String(s.step).padStart(2, "0")}
                </div>
                <div className="mt-4 font-serif text-sm" style={{ color: "var(--burgundy)" }}>{s.name}</div>
                <div className="text-[11px] mt-1 leading-tight" style={{ color: "var(--text-muted)" }}>{s.verb}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Course catalog</div>
          <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: "var(--burgundy)" }}>Currently offered.</h2>
        </div>
        {courses.length > 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{courses.length} course{courses.length === 1 ? "" : "s"}</div>}
      </div>

      {loading ? null : courses.length === 0 ? (
        <div className="rounded-2xl border p-10 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
          <div className="eyebrow mb-2">Coming soon</div>
          <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>Learning Circle offerings are being prepared.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((c) => {
            const stage = STAGES.find((s) => s.step === c.stage) || STAGES[0];
            const prog = progressFor(c.id);
            return (
              <Link key={c.id} to={`/learn/${c.id}`} data-testid={`course-card-${c.id}`}
                className="lift rounded-2xl overflow-hidden border block" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
                {(c.art_url) && (
                  <div className="relative h-44">
                    <img src={assetUrl(c.art_url)} alt={c.title} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" style={{ background: "rgba(84,28,43,0.9)", color: "var(--gold)" }}>
                      Stage {String(c.stage).padStart(2, "0")} · {stage.name}
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-serif text-xl leading-tight" style={{ color: "var(--burgundy)" }}>{c.title}</h3>
                  {(c.tagline || c.subtitle) && <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--text-muted)" }}>{c.tagline || c.subtitle}</p>}
                  <div className="mt-4 flex items-center gap-4 text-xs" style={{ color: "var(--text-muted)" }}>
                    {c.duration && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{c.duration}</span>}
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{c.lesson_count} Encounter{c.lesson_count === 1 ? "" : "s"}</span>
                  </div>
                  {prog > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-[11px] mb-1 font-mono" style={{ color: "var(--gold-dark)" }}>
                        <span>PROGRESS</span><span>{prog}%</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: "var(--border-cream)" }}>
                        <div className="h-full rounded-full" style={{ width: `${prog}%`, background: "var(--burgundy)" }} />
                      </div>
                    </div>
                  )}
                  <div className="mt-5 inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--burgundy)" }}>
                    {prog > 0 ? "Continue" : "Begin course"} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
